import { engeniApi } from '@/api/engeni.api';
import type {
  ApiResponse,
  ApiResponseData,
  ApiResponsePaginated,
} from '@/interfaces/api.interface';
import {
  EcommerceStatus,
  type Ecommerce,
  type EcommerceGoogleAdsIntegration,
  type EcommerceGoogleAnalyticsIntegration,
  type EcommerceGoogleMerchantIntegration,
  type EcommerceGoogleTagManagerIntegration,
  type EcommerceIntegration,
} from '@/interfaces/ecommerce.interface';
import type { Sale } from '@/interfaces/sale.interface';
import type { Product } from '@/interfaces/product.interface';
import type {
  EcommerceStats,
  ProductsStats,
  SalesStats,
  SummaryStats,
} from '@/interfaces/stats.interface';
import type {
  AuthorizeWithCode,
  AuthorizeWithToken,
  CreatePlanSubscriptionApprovalDTO,
  EcommerceScoreDTO,
  SetupEcommerceAnalyticsIntegrationDTO,
  UpdateEcommerceDTO,
} from './flok.service.interface';
import type { ConfigurationTask } from '@/interfaces/configurationTask.interface';
import type { ProductsMonitor } from '@/interfaces/monitor.interface';
import type { PlanSubscriptionApproval } from '@/interfaces/planSubscriptionApproval.interface';
import type { EcommerceProvider } from '@/interfaces/ecommerceProvider.interface';
import type { ProductSuggestion } from '@/interfaces/productSuggestion.interface';
import type { Issue, IssueStatus } from '@/interfaces/issue.interface';
import type {
  GoogleAnalyticsAllowedAccount,
  GoogleAnalyticsAllowedDataStream,
  GoogleAnalyticsAllowedProperty,
} from '@/interfaces/analyticsProperty.interface';
import type { EcommerceWidget, Widget, WidgetSlug } from '@/interfaces/widget.interface';
import type { Checkpoint, CheckpointSlug } from '@/interfaces/checkpoint.interface';
import type { EcommerceSuccessCalculator } from '@/interfaces/successCalculator.interface';
import { buildV2ListParams } from '@/utils/v2List.utils';
import type {
  DiagnosticRunStart,
  DiagnosticRunStatus,
  DiagnosticRepairResult,
} from '@/modules/flokee/interfaces/googleDiagnostic.interface';
import type {
  GoogleMerchantCompetitorCategory,
  GoogleMerchantCompetitorsQuery,
  GoogleMerchantCompetitorsResponse,
  GoogleMerchantDiagnosticsResponse,
  GoogleMerchantInsightsResponse,
  GoogleMerchantIssuesResponse,
  GoogleMerchantProductCategory,
  GoogleMerchantProductDetail,
  GoogleMerchantProductPerformanceResponse,
  GoogleMerchantProductPricingResponse,
  GoogleMerchantProductsQuery,
  GoogleMerchantProductsResponse,
  GoogleMerchantStatusHistoryEntry,
} from '@/modules/flokee/interfaces/googleMerchantAdmin.interface';

const BASE_PATH = '/flok';
//const BASE_PUBLIC_PATH = `/public${BASE_PATH}`;

export const flokService = {
  async authorizeEcommerce(ecommerce_id: number, data: AuthorizeWithCode | AuthorizeWithToken) {
    return await engeniApi.post(`${BASE_PATH}/ecommerces/${ecommerce_id}/authorize`, data);
  },
  async getEcommercesByAccountId(account_id: number) {
    const params = buildV2ListParams({
      filter: {
        account_id,
        status: [
          EcommerceStatus.ACTIVE,
          EcommerceStatus.PENDING,
          EcommerceStatus.SUSPENDED,
          EcommerceStatus.TEST,
        ],
      },
      pagination: {
        no_pagination: true,
      },
    });

    return await engeniApi.get<ApiResponseData<Ecommerce[]>>(
      `${BASE_PATH}/ecommerces`,
      {
        params,
      },
    );
  },
  async updateEcommerce(ecommerce_id: number, data: Partial<UpdateEcommerceDTO>) {
    return await engeniApi.put<ApiResponseData<Ecommerce>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}`,
      data,
    );
  },
  async getEcommerceSummaryStats(ecommerce_id: number, filter?: Record<string, any>) {
    return await engeniApi.get<ApiResponseData<EcommerceStats<SummaryStats>>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/stats/summary`,
      {
        params: { ...filter },
      },
    );
  },
  async getEcommerceProductStats(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<EcommerceStats<ProductsStats>>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/stats/products`,
    );
  },
  async getEcommerceSalesStats(ecommerce_id: number, filter?: Record<string, any>) {
    return await engeniApi.get<ApiResponseData<EcommerceStats<SalesStats>>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/stats/sales`,
      {
        params: { ...filter },
      },
    );
  },
  async getEcommerceSalesByProduct(
    ecommerce_id: number,
    filter?: Record<string, any>,
    pagination?: { page: number; per_page: number },
  ) {
    const { sort, ...filters } = filter ?? {};
    const params = buildV2ListParams({
      filter: filters,
      pagination,
      sort,
    });

    return await engeniApi.get<ApiResponsePaginated<any[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/stats/sales-by-product`,
      {
        params,
      },
    );
  },

  async downloadEcommerceSalesByProductCsv(
    ecommerce_id: number,
    filter?: Record<string, any>,
  ): Promise<Blob> {
    const response = await engeniApi.get(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/stats/sales-by-product/csv`,
      {
        params: { ...filter },
        responseType: 'blob',
      },
    );

    return response.data;
  },
  async getEcommerceProducts(
    ecommerce_id: number,
    filter: Record<string, any>,
    pagination: { page: number; per_page: number },
  ) {
    const params = buildV2ListParams({
      filter: { ...filter, ecommerce_id },
      pagination,
      sort: 'availability',
    });

    return await engeniApi.get<ApiResponsePaginated<Product[]>>(
      `${BASE_PATH}/products`,
      {
        params,
      },
    );
  },
  async updateProduct(product_id: number, data: Partial<Product>) {
    return await engeniApi.put<ApiResponseData<Product>>(
      `${BASE_PATH}/products/${product_id}`,
      data,
    );
  },
  async syncEcommerceProducts(ecommerce_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/products/sync`,
    );
  },
  async getEcommerceSales(
    ecommerce_id: number,
    filter: Record<string, any>,
    pagination: { page: number; per_page: number },
  ) {
    const params = buildV2ListParams({
      filter,
      pagination,
    });

    return await engeniApi.get<ApiResponsePaginated<Sale[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/sales`,
      {
        params,
      },
    );
  },
  async syncEcommerceSales(ecommerce_id: number) {
    const toDate = new Date();
    const fromDate = new Date(toDate);
    fromDate.setDate(toDate.getDate() - 3);

    const from = fromDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    const to = toDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    return await engeniApi.post<ApiResponse>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/sales/sync?from=${from}&to=${to}`,
    );
  },
  async getEcommerceProviderIntegration(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<EcommerceIntegration>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/provider`,
    );
  },
  async setupEcommerceTagManagerIntegration(
    ecommerce_id: number,
    data: { external_account: boolean },
  ) {
    return await engeniApi.post<ApiResponseData<EcommerceGoogleTagManagerIntegration>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/tag-manager/setup`,
      data,
    );
  },
  async setupEcommerceMerchantIntegration(
    ecommerce_id: number,
    data: { external_account: boolean },
  ) {
    return await engeniApi.post<ApiResponseData<EcommerceGoogleMerchantIntegration>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/google-merchant/setup`,
      data,
    );
  },
  async setupEcommerceAnalyticsIntegration(
    ecommerce_id: number,
    data: SetupEcommerceAnalyticsIntegrationDTO,
  ) {
    return await engeniApi.post<ApiResponseData<EcommerceGoogleAnalyticsIntegration>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/analytics/setup`,
      data,
    );
  },
  async authorizeGoogleAnalyticsIntegration(ecommerce_id: number, data: { code: string }) {
    return await engeniApi.get<ApiResponse>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/analytics/auth/callback`,
      {
        params: { code: data.code },
      },
    );
  },
  async setupEcommerceGoogleAdsIntegration(ecommerce_id: number) {
    return await engeniApi.post<ApiResponseData<EcommerceGoogleAdsIntegration>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/ads/setup`,
    );
  },
  async getConfigurationTasks(ecommerce_id: number, filter?: Record<string, any>) {
    const params = buildV2ListParams({
      filter,
      pagination: {
        no_pagination: true,
      },
    });

    return await engeniApi.get<ApiResponseData<ConfigurationTask[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/configuration-tasks`,
      {
        params,
      },
    );
  },
  async updateConfigurationTask(
    ecommerce_id: number,
    configuration_task_id: number,
    data: Partial<ConfigurationTask>,
  ) {
    return await engeniApi.put<ApiResponseData<ConfigurationTask>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/configuration-tasks/${configuration_task_id}`,
      data,
    );
  },
  async getProductsMonitor(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<ProductsMonitor>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/stats/monitors?filter_by=products_monitor`,
    );
  },
  async getPlanSubscriptionApprovals(ecommerce_id: number) {
    const params = buildV2ListParams({
      pagination: {
        no_pagination: true,
      },
    });

    return await engeniApi.get<ApiResponseData<PlanSubscriptionApproval[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/plan_subscription_approvals`,
      {
        params,
      },
    );
  },
  async createPlanSubscriptionApproval(
    ecommerce_id: number,
    data: CreatePlanSubscriptionApprovalDTO,
  ) {
    return await engeniApi.post<ApiResponseData<PlanSubscriptionApproval>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/plan_subscription_approvals`,
      data,
    );
  },
  async getEcommerceProviders(params: { country?: string } = {}) {
    const listParams = buildV2ListParams({
      filter: {
        enabled: true,
      },
      pagination: {
        no_pagination: true,
      },
    });

    return await engeniApi.get<ApiResponseData<EcommerceProvider[]>>(
      `${BASE_PATH}/providers`,
      {
        params: {
          ...listParams,
          ...params,
        },
      },
    );
  },
  async getProductSuggestions(ecommerce_id: number, product_id: number) {
    return await engeniApi.get<ApiResponseData<ProductSuggestion>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/products/${product_id}/suggestions`,
    );
  },
  async getCheckpointBySlug(slug: CheckpointSlug) {
    return await engeniApi.get<ApiResponseData<Checkpoint>>(`${BASE_PATH}/checkpoints/${slug}`);
  },
  async getEcommerceScore(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<EcommerceScoreDTO>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/score`,
    );
  },
  async getEcommerceIssuesByCheckpointId(
    ecommerce_id: number,
    filter: Record<string, any>,
    pagination: { page: number; per_page: number },
  ) {
    const params = buildV2ListParams({
      filter,
      pagination,
    });

    return await engeniApi.get<ApiResponsePaginated<Issue[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/issues`,
      {
        params,
      },
    );
  },
  async executeCheckpoint(ecommerce_id: number, checkpoint_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/checkpoints/${checkpoint_id}/execute`,
    );
  },
  async updateCheckpointIssues(
    ecommerce_id: number,
    checkpoint_id: number,
    data: {
      status: IssueStatus;
      reason?: string;
    },
  ) {
    return await engeniApi.put<ApiResponse>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/checkpoints/${checkpoint_id}/issues`,
      data,
    );
  },
  async getAnalyticsAllowedAccounts(ecommerce_id: number) {
    return await engeniApi.get<GoogleAnalyticsAllowedAccount[]>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/analytics/allowed-accounts`,
    );
  },
  async getAnalyticsAllowedProperties(ecommerce_id: number, account_id: string | number) {
    return await engeniApi.get<GoogleAnalyticsAllowedProperty[]>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/analytics/allowed-properties`,
      {
        params: { account_id },
      },
    );
  },
  async getAnalyticsAllowedDataStreams(ecommerce_id: number, property_id: string | number) {
    return await engeniApi.get<GoogleAnalyticsAllowedDataStream[]>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/integrations/google/analytics/allowed-data-streams`,
      {
        params: { property_id },
      },
    );
  },
  async getDiagnostic(ecommerce_id: number, product: string) {
    return await engeniApi.get<ApiResponseData<DiagnosticRunStart>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/diagnostics/${product}`,
    );
  },
  async getGoogleMerchantDiagnostics(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantDiagnosticsResponse>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/diagnostics`,
    );
  },
  async getGoogleMerchantProducts(ecommerce_id: number, params: GoogleMerchantProductsQuery = {}) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantProductsResponse>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/products`,
      { params },
    );
  },
  async getGoogleMerchantIssues(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantIssuesResponse>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/issues`,
    );
  },
  async getGoogleMerchantInsights(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantInsightsResponse>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/insights`,
    );
  },
  async getGoogleMerchantCompetitors(
    ecommerce_id: number,
    params: GoogleMerchantCompetitorsQuery = {},
  ) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantCompetitorsResponse>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/competitors`,
      { params },
    );
  },
  async getGoogleMerchantCompetitorCategories(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantCompetitorCategory[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/categories`,
    );
  },
  async getGoogleMerchantProductCategories(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantProductCategory[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/product-categories`,
    );
  },
  async getGoogleMerchantProduct(ecommerce_id: number, product_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantProductDetail>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/products/${product_id}`,
    );
  },
  async getGoogleMerchantProductStatusHistory(ecommerce_id: number, product_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantStatusHistoryEntry[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/products/${product_id}/status-history`,
    );
  },
  async getGoogleMerchantProductPerformance(ecommerce_id: number, product_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantProductPerformanceResponse>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/products/${product_id}/performance`,
    );
  },
  async getGoogleMerchantProductPricing(ecommerce_id: number, product_id: number) {
    return await engeniApi.get<ApiResponseData<GoogleMerchantProductPricingResponse>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/google-merchant/products/${product_id}/price-benchmark`,
    );
  },
  async startDiagnosticRun(ecommerce_id: number, product: string) {
    return await engeniApi.post<ApiResponseData<DiagnosticRunStart>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/diagnostics/${product}/run`,
    );
  },
  async getDiagnosticRunStatus(ecommerce_id: number, product: string, run_id: string) {
    return await engeniApi.get<ApiResponseData<DiagnosticRunStatus>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/diagnostics/${product}/run/${run_id}`,
    );
  },
  async repairDiagnostic(ecommerce_id: number, product: string, fixes?: string[]) {
    return await engeniApi.post<ApiResponseData<DiagnosticRepairResult>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/diagnostics/${product}/repair`,
      fixes?.length ? { fixes } : {},
    );
  },
  async getWidgets() {
    return await engeniApi.get<ApiResponseData<Widget[]>>(`${BASE_PATH}/widgets`);
  },
  async getEcommerceWidgets(ecommerce_id: number) {
    return await engeniApi.get<ApiResponseData<EcommerceWidget[]>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/widgets`,
    );
  },
  async installEcommerceWidget(ecommerce_id: number, widget_slug: WidgetSlug) {
    return await engeniApi.post<ApiResponseData<EcommerceWidget>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/widgets/`,
      {
        widget_slug,
        config: null,
      },
    );
  },
  async updateEcommerceWidget(ecommerce_id: number, data: Partial<EcommerceWidget>) {
    return await engeniApi.post<ApiResponseData<EcommerceWidget>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/widgets`,
      data,
    );
  },
  async publishEcommerceWidgets(ecommerce_id: number) {
    return await engeniApi.post<ApiResponse>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/widgets/publish`,
    );
  },
  async toggleEcommerceWidget(
    ecommerce_id: number,
    widget_slug: string,
    data: { enabled: boolean },
  ) {
    return await engeniApi.patch<ApiResponseData<Widget>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/widgets/${widget_slug}/enabled`,
      data,
    );
  },
  async setApplicableEcommerceWidget(
    ecommerce_id: number,
    widget_slug: WidgetSlug,
    data: { applicable: boolean },
  ) {
    return await engeniApi.patch<ApiResponseData<EcommerceWidget>>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/widgets/${widget_slug}/applicable`,
      data,
    );
  },
  async getEcommerceSuccessCalculator(ecommerce_id: number) {
    return await engeniApi.post<EcommerceSuccessCalculator>(
      `${BASE_PATH}/ecommerces/${ecommerce_id}/success-calculator`,
    );
  },
};
